import Cookies from 'universal-cookie';
// routes
import { PATH_AUTH } from '../routes/paths';
// utils
import axios from '../utils/axios';
import { AuthService } from '../api/auth.service';
import { bindTabSession, syncTabSession } from './sessionSync';

// ----------------------------------------------------------------------

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  const decoded = jwtDecode(accessToken);

  const currentTime = Date.now() / 1000;

  return decoded.exp > currentTime;
};

// ----------------------------------------------------------------------

// Refresh this long before the access token expires, so requests in flight
// never go out with an expired token.
const REFRESH_LEAD_MS = 60 * 1000;
const REFRESH_RETRY_MS = 30 * 1000;

// Module-level so a new session (login, refresh) replaces the previous timer
// instead of stacking another one.
let expiredTimer: ReturnType<typeof setTimeout> | undefined;

const removeSessionCookies = () => {
  const cookies = new Cookies();
  [undefined, process.env.REACT_APP_COOKIE_DOMAIN, window.location.hostname]
    .filter((domain, i, all) => all.indexOf(domain) === i)
    .forEach((domain) => {
      const opts = domain ? { path: '/', domain } : { path: '/' };
      cookies.remove(process.env.REACT_APP_TOKEN!, opts);
      cookies.remove(process.env.REACT_APP_REFRESH_TOKEN!, opts);
    });
};

const clearSessionAndRedirect = () => {
  removeSessionCookies();
  delete axios.defaults.headers.common.Authorization;
  bindTabSession(null);
  window.location.href = PATH_AUTH.login;
};

const isUsableToken = (token?: string) => {
  try {
    return !!token && isValidToken(token);
  } catch {
    return false;
  }
};

const refreshSession = async () => {
  // Another tab may have signed in as someone else; never refresh (and so
  // overwrite the shared cookie) on behalf of a stale identity.
  if (syncTabSession()) return;
  const cookies = new Cookies();
  const refreshToken: string | undefined = cookies.get(
    process.env.REACT_APP_REFRESH_TOKEN!,
  );
  if (!isUsableToken(refreshToken)) {
    clearSessionAndRedirect();
    return;
  }
  try {
    // POST /auth/refresh reads the refresh token from the Bearer header
    // (JwtAuthGuard) and answers like login: { accessToken, refreshToken, user }.
    const { data } = await new AuthService().getRefreshToken(
      refreshToken as string,
    );
    if (!data?.accessToken || !data?.refreshToken) {
      throw new Error('Invalid refresh response');
    }
    setSession(data.accessToken, data.refreshToken);
  } catch (error) {
    const statusCode = (error as { statusCode?: number } | undefined)
      ?.statusCode;
    const rejected = statusCode === 401 || statusCode === 403;
    // Network blips: retry while the refresh token is still valid.
    if (!rejected && isUsableToken(refreshToken)) {
      clearTimeout(expiredTimer);
      expiredTimer = setTimeout(refreshSession, REFRESH_RETRY_MS);
      return;
    }
    clearSessionAndRedirect();
  }
};

export const tokenExpired = (exp: number) => {
  const timeLeft = Math.max(exp * 1000 - Date.now() - REFRESH_LEAD_MS, 0);
  clearTimeout(expiredTimer);
  expiredTimer = setTimeout(refreshSession, timeLeft);
};

// ----------------------------------------------------------------------

export const setSession = (
  accessToken: string | null,
  refreshToken: string | null,
) => {
  const cookies = new Cookies();
  if (accessToken && refreshToken) {
    const in3Hours = new Date(new Date().getTime() + 1000 * 60 * 60 * 3);
    const in2Days = new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 2);
    const optsAuthToken = {
      expires: in3Hours,
      domain: process.env.REACT_APP_COOKIE_DOMAIN,
      path: '/',
    };

    const optsRefreshToken = {
      expires: in2Days,
      domain: process.env.REACT_APP_COOKIE_DOMAIN,
      path: '/',
    };
    cookies.set(process.env.REACT_APP_TOKEN!, accessToken, optsAuthToken);
    cookies.set(
      process.env.REACT_APP_REFRESH_TOKEN!,
      refreshToken,
      optsRefreshToken,
    );
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    bindTabSession(accessToken);

    // This function below will handle when token is expired
    const { exp } = jwtDecode(accessToken); // ~1 days by minimals server
    tokenExpired(exp);
  } else {
    clearTimeout(expiredTimer);
    removeSessionCookies();
    bindTabSession(null);
    delete axios.defaults.headers.common.Authorization;
  }
};
